import React from 'react';
import { Box } from '@mui/material';
import { NavLink, Outlet } from 'react-router-dom';
import { H4 } from '../../components/typography';
import Spacer from '../../components/Spacer';
import HyriaLogo from '../../assets/hyria-logo.svg';
import { BarChart3, FileText, Users, Download, UserCheck, Search, Building } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { signOut } from 'firebase/auth';
import { auth, db } from '../../config/firebase';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { doc, getDoc, collection, query, where, onSnapshot } from 'firebase/firestore';
import { User, ChevronDown, Home } from 'lucide-react';

const navItems = [
  { label: 'Översikt', path: '', icon: BarChart3 },
  { label: 'Ansökningar', path: 'applications', icon: FileText },
  { label: 'Föreningar', path: 'associations', icon: Building },
  { label: 'Lägenhets-claims', path: 'apartment-claims', icon: Home },
  { label: 'Kreditkontroller', path: 'credit-checks', icon: FileText },
  { label: 'Folkbokföring', path: 'audit', icon: Search },
  { label: 'Rapporter', path: 'reports', icon: Users },
  { label: 'Inställningar', path: 'settings', icon: UserCheck },
];

function UserMenu() {
  const [user, setUser] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [isLoadingUserData, setIsLoadingUserData] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (u) => {
      setUser(u);
      if (u) {
        setIsLoadingUserData(true);
        try {
          const userRef = doc(db, 'users', u.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) setUserInfo(userSnap.data());
        } catch (error) {
        } finally {
          setIsLoadingUserData(false);
        }
      } else {
        setIsLoadingUserData(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/sign-in');
  };

  if (!user) return null;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button className="absolute top-6 right-8 flex items-center gap-2 bg-white rounded-full shadow-md px-3 py-2 hover:bg-zinc-50 transition focus:outline-none">
          <User className="w-6 h-6 text-[#2563eb]" />
          <span className="hidden md:block text-zinc-700 font-medium text-sm">
            {isLoadingUserData 
              ? 'Laddar...' 
              : userInfo?.firstName 
                ? `${userInfo.firstName} ${userInfo.lastName}` 
                : user.email
            }
          </span>
          <ChevronDown className="w-4 h-4 text-zinc-500" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content sideOffset={8} className="bg-white rounded-xl shadow-lg p-2 min-w-[180px] z-50">
        <DropdownMenu.Item
          className="px-4 py-2 rounded-lg text-zinc-700 hover:bg-zinc-100 cursor-pointer text-sm"
          onClick={() => navigate('/hyria-dashboard/settings')}
        >
          Inställningar
        </DropdownMenu.Item>
        <DropdownMenu.Separator className="my-1 h-px bg-zinc-100" />
        <DropdownMenu.Item
          className="px-4 py-2 rounded-lg text-red-600 hover:bg-red-50 cursor-pointer text-sm"
          onClick={handleLogout}
        >
          Logga ut
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

export default function HyriaDashboardLayout() {
  // Realtidsräknare för väntande lägenhets-claims — visas som röd badge
  // på "Lägenhets-claims"-menyposten så Hyria-admin inte missar nya ärenden.
  const [pendingClaimsCount, setPendingClaimsCount] = useState(0);

  useEffect(() => {
    const q = query(
      collection(db, 'housing'),
      where('ownershipStatus', '==', 'PENDING_APPROVAL')
    );
    const unsub = onSnapshot(q, (snap) => {
      setPendingClaimsCount(snap.size);
    }, () => {
      // Ignorera permission-errors (t.ex. om användaren inte är inloggad än)
      setPendingClaimsCount(0);
    });
    return () => unsub();
  }, []);

  return (
    <Box className="flex min-h-screen bg-gray-50 font-sans" style={{ fontFamily: 'Poppins, sans-serif' }}>
      {/* Sidebar */}
      <Box className="w-72 bg-white shadow-lg rounded-r-2xl flex flex-col p-8 border-r border-gray-200">
        <Box className="flex flex-col items-center mb-8">
          <img src={HyriaLogo} alt="Hyria logo" className="h-12 w-auto mb-2" />
          <H4 className="font-bold tracking-tight text-[#e74c3c] text-2xl mt-2">Hyria Admin</H4>
          <div className="text-sm text-zinc-500 mt-1">Kreditkontroller & Rapporter</div>
        </Box>
        <nav className="flex flex-col gap-2 mt-4">
          {navItems.map(item => {
            const IconComponent = item.icon;
            const badgeCount = item.path === 'apartment-claims' ? pendingClaimsCount : 0;
            return (
              <NavLink
                key={item.path}
                to={item.path === '' ? '/hyria-dashboard' : `/hyria-dashboard/${item.path}`}
                className={({ isActive }) =>
                  `px-6 py-3 rounded-2xl font-medium transition-all text-base flex items-center gap-4 min-h-[52px]
                  ${isActive
                    ? 'bg-[#e74c3c] text-white font-bold shadow-md'
                    : 'text-zinc-500 hover:bg-[#e74c3c]/10 hover:text-black'}`
                }
                end
              >
                {IconComponent ? <IconComponent className="w-6 h-6 flex-shrink-0" /> : <div className="w-6 h-6 bg-red-500 rounded flex-shrink-0"></div>}
                <span className="flex-1">{item.label}</span>
                {badgeCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 text-[11px] font-bold text-white bg-red-500 rounded-full flex-shrink-0">
                    {badgeCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
        <Spacer spacing={8} />
        
        {/* Quick Actions för Hyria */}
        <div className="mt-auto">
          <div className="text-sm font-semibold text-zinc-600 mb-3">Snabbåtgärder</div>
          <div className="space-y-2">
            <button className="w-full text-left px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-100 rounded-lg transition">
              <Search className="w-4 h-4 inline mr-2" />
              Sök ansökan
            </button>
            <button className="w-full text-left px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-100 rounded-lg transition">
              <Download className="w-4 h-4 inline mr-2" />
              Ladda rapport
            </button>
          </div>
        </div>
      </Box>
      <UserMenu />
      {/* Main Content */}
      <Box className="flex-1 p-8 bg-gray-50">
        <Outlet />
      </Box>
    </Box>
  );
}
